import { BaseSensor, SensorHealth } from '../../types/sensor';
import { AudioSensorConfig, AudioFeatureWindow } from '../../types/audio';
import { DeviceInfo } from '../../types/session';

export class MicrophoneFeaturesSensor implements BaseSensor<AudioSensorConfig, AudioFeatureWindow> {
  readonly id: string;
  readonly name: string;

  private config: AudioSensorConfig = {
    windowSizeMs: 100,
    sampleRate: 44100,
    fftSize: 2048,
    requestEchoCancellation: false,
    requestAutoGainControl: false,
    requestNoiseSuppression: false,
  };

  private audioCtx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;

  private isRunning = false;
  private timerId: number | null = null;
  private sequence = 0;
  private callbacks: Array<(data: AudioFeatureWindow) => void> = [];

  // Diagnostics
  private status: SensorHealth['status'] = 'idle';
  private lastEmitMs = 0;
  private droppedCount = 0;
  private errorCount = 0;
  private lastError?: string;
  private qualityFlags: Set<string> = new Set();

  private actualSettings: {
    echoCancellation?: boolean;
    autoGainControl?: boolean;
    noiseSuppression?: boolean;
    sampleRate?: number;
  } = {};

  constructor(id = 'audio.microphone-features', name = 'Microphone Features') {
    this.id = id;
    this.name = name;
  }

  describe() {
    return {
      id: this.id,
      name: this.name,
      version: '1.0.0',
      description: 'Web Audio API Microphone Feature Extractor (RMS, 相对于数字满刻度的分贝 dBFS, F0, Spectral Centroid, ZCR)',
    };
  }

  configure(config: Partial<AudioSensorConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): AudioSensorConfig {
    return { ...this.config };
  }

  static async enumerateAudioDevices(): Promise<DeviceInfo[]> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      throw new Error('MediaDevices API 不受当前浏览器支持，或未在 HTTPS / localhost 环境下运行。');
    }

    let devices = await navigator.mediaDevices.enumerateDevices();
    let audioInputs = devices.filter((d) => d.kind === 'audioinput');

    if (audioInputs.length > 0 && audioInputs.some((d) => !d.label)) {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        tempStream.getTracks().forEach((track) => track.stop());
        devices = await navigator.mediaDevices.enumerateDevices();
        audioInputs = devices.filter((d) => d.kind === 'audioinput');
      } catch (e) {
        console.warn('Unable to request temp mic permission for labels:', e);
      }
    }

    return audioInputs.map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label || `麦克风 ${i + 1} (${d.deviceId.slice(0, 8)})`,
      kind: 'audioinput',
    }));
  }

  async start(): Promise<void> {
    if (this.isRunning) return;

    this.status = 'configuring';
    this.qualityFlags.clear();
    this.qualityFlags.add('analyser-node-processing');
    this.sequence = 0;

    const constraints: MediaStreamConstraints = {
      audio: {
        deviceId: this.config.deviceId ? { exact: this.config.deviceId } : undefined,
        echoCancellation: this.config.requestEchoCancellation,
        autoGainControl: this.config.requestAutoGainControl,
        noiseSuppression: this.config.requestNoiseSuppression,
      },
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      const track = this.stream.getAudioTracks()[0];
      const settings = track.getSettings();

      this.actualSettings = {
        echoCancellation: settings.echoCancellation,
        autoGainControl: settings.autoGainControl,
        noiseSuppression: settings.noiseSuppression,
        sampleRate: settings.sampleRate,
      };

      if (settings.echoCancellation) this.qualityFlags.add('echo-cancellation-active');
      if (settings.autoGainControl) this.qualityFlags.add('agc-active');
      if (settings.noiseSuppression) this.qualityFlags.add('noise-suppression-active');

      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      this.audioCtx = new AudioCtx();

      if (this.audioCtx.state === 'suspended') {
        await this.audioCtx.resume();
      }

      this.sourceNode = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyserNode = this.audioCtx.createAnalyser();
      this.analyserNode.fftSize = this.config.fftSize;
      this.analyserNode.smoothingTimeConstant = 0.2;

      this.sourceNode.connect(this.analyserNode);

      this.isRunning = true;
      this.status = 'running';

      this.timerId = window.setInterval(() => this.processAudioWindow(), this.config.windowSizeMs);
    } catch (err: any) {
      this.status = 'error';
      this.errorCount++;
      this.lastError = err.message || String(err);
      this.qualityFlags.add('microphone-unavailable');
      throw new Error(`麦克风启动失败: ${this.lastError}`);
    }
  }

  private processAudioWindow(): void {
    if (!this.analyserNode || !this.audioCtx || !this.isRunning) return;

    const observedAt = Date.now();
    const monotonicMs = performance.now();
    const sampleRate = this.audioCtx.sampleRate;

    const timeDomainData = new Float32Array(this.analyserNode.fftSize);
    const freqDomainData = new Float32Array(this.analyserNode.frequencyBinCount);

    this.analyserNode.getFloatTimeDomainData(timeDomainData);
    this.analyserNode.getFloatFrequencyData(freqDomainData);

    // 1. RMS Calculation
    let sumSquares = 0;
    let zeroCrossings = 0;
    let maxAbs = 0;

    for (let i = 0; i < timeDomainData.length; i++) {
      const val = timeDomainData[i];
      sumSquares += val * val;
      if (Math.abs(val) > maxAbs) maxAbs = Math.abs(val);

      if (i > 0 && ((timeDomainData[i - 1] >= 0 && val < 0) || (timeDomainData[i - 1] < 0 && val >= 0))) {
        zeroCrossings++;
      }
    }

    const rms = Math.sqrt(sumSquares / timeDomainData.length);

    // 2. dBFS Calculation (相对于数字满刻度的分贝)
    const dbfs = Math.max(-120, Math.round(20 * Math.log10(rms + 1e-12) * 10) / 10);

    // 3. Dominant Frequency F0 (Autocorrelation)
    const dominantFrequencyHz = this.calculateAutocorrelationF0(timeDomainData, sampleRate);

    // 4. Spectral Centroid Calculation
    let num = 0;
    let den = 0;
    const nyquist = sampleRate / 2;
    const binSize = nyquist / freqDomainData.length;

    for (let i = 0; i < freqDomainData.length; i++) {
      const mag = Math.pow(10, freqDomainData[i] / 20);
      const freq = i * binSize;
      num += freq * mag;
      den += mag;
    }
    const spectralCentroidHz = den > 1e-6 ? Math.round(num / den) : 0;

    const zcr = Math.round((zeroCrossings / timeDomainData.length) * 1000) / 1000;

    let windowStatus: AudioFeatureWindow['status'] = 'ok';
    if (maxAbs >= 0.99) {
      windowStatus = 'clipping';
    } else if (rms < 1e-4) {
      windowStatus = 'silent';
    }

    this.sequence++;
    this.lastEmitMs = monotonicMs;

    const featureWindow: AudioFeatureWindow = {
      sequence: this.sequence,
      observedAt,
      monotonicMs,
      rms: Math.round(rms * 10000) / 10000,
      dbfs,
      dominantFrequencyHz,
      spectralCentroidHz,
      zeroCrossingRate: zcr,
      sampleCount: timeDomainData.length,
      audioDeviceId: this.config.deviceId || 'default',
      status: windowStatus,
      qualityFlags: Array.from(this.qualityFlags),
    };

    for (const cb of this.callbacks) {
      cb(featureWindow);
    }
  }

  private calculateAutocorrelationF0(buffer: Float32Array, sampleRate: number): number {
    const minFreq = 50;
    const maxFreq = 2000;
    const minLag = Math.floor(sampleRate / maxFreq);
    const maxLag = Math.floor(sampleRate / minFreq);

    let bestLag = -1;
    let maxAutocorr = -1;

    let energy = 0;
    for (let i = 0; i < buffer.length; i++) energy += buffer[i] * buffer[i];
    if (energy < 1e-5) return 0;

    for (let lag = minLag; lag <= maxLag; lag++) {
      let sum = 0;
      for (let i = 0; i < buffer.length - lag; i++) {
        sum += buffer[i] * buffer[i + lag];
      }
      if (sum > maxAutocorr) {
        maxAutocorr = sum;
        bestLag = lag;
      }
    }

    if (bestLag > 0 && maxAutocorr / energy > 0.3) {
      return Math.round(sampleRate / bestLag);
    }

    return 0;
  }

  async stop(): Promise<void> {
    this.isRunning = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.audioCtx) {
      await this.audioCtx.close();
      this.audioCtx = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    this.status = 'stopped' as any;
  }

  health(): SensorHealth {
    return {
      status: this.status,
      lastEmitMonotonicMs: this.lastEmitMs,
      droppedCount: this.droppedCount,
      errorCount: this.errorCount,
      lastErrorMessage: this.lastError,
      qualityFlags: Array.from(this.qualityFlags),
    };
  }

  getActualSettings() {
    return { ...this.actualSettings };
  }

  onData(callback: (data: AudioFeatureWindow) => void): void {
    this.callbacks.push(callback);
  }
}
